import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { usePageState } from '@confluence/page-context';

import type { ContentVersionHistoryQuery_contentVersionHistory_nodes as VersionHistoryNodeType } from './graphql/__types__/ContentVersionHistoryQuery';

type UseVersionHistoryAnalyticsOutputType = {
	onVersionClick: (version: VersionHistoryNodeType | null) => void;
	onVersionHistoryMenuOpen: () => void;
	onCopyVersionContent: (version: VersionHistoryNodeType) => void;
	onRestore: (version: VersionHistoryNodeType) => void;
	onRestoreModalClose: () => void;
};

export const useVersionHistoryAnalytics = (): UseVersionHistoryAnalyticsOutputType => {
	const [{ contentType, contentId }] = usePageState();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onVersionClick = useCallback(
		(version: VersionHistoryNodeType | null) => {
			if (version) {
				const { updatedTime, number } = version;

				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'version',
						actionSubjectId: 'selected',
						source: 'versionHistoryScreen',
						objectId: contentId,
						objectType: contentType,
						attributes: {
							updatedTime,
							number,
						},
					},
				}).fire();
			} else {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'version',
						actionSubjectId: 'current',
						source: 'versionHistoryScreen',
						objectId: contentId,
						objectType: contentType,
					},
				}).fire();
			}
		},
		[contentId, contentType, createAnalyticsEvent],
	);

	const onVersionHistoryMenuOpen = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'versionHistoryScreen',
				attributes: {
					objectType: contentType,
					objectId: contentId,
				},
			},
		}).fire();
	}, [contentId, contentType, createAnalyticsEvent]);

	const onCopyVersionContent = useCallback(
		(version: VersionHistoryNodeType) => {
			const { updatedTime, number } = version;
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'copyVersionContent',
					source: 'versionHistoryScreen',
					objectId: contentId,
					objectType: contentType,
					attributes: {
						updatedTime,
						number,
					},
				},
			}).fire();
		},
		[contentId, contentType, createAnalyticsEvent],
	);

	const onRestore = useCallback(
		(version: VersionHistoryNodeType) => {
			const { updatedTime, number } = version;
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'restore',
					objectId: contentId,
					objectType: contentType,
					source: 'versionHistoryModal',
					attributes: {
						updatedTime,
						number,
					},
				},
			}).fire();
		},
		[contentId, contentType, createAnalyticsEvent],
	);

	const onRestoreModalClose = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'closeButton',
				source: 'versionHistoryScreen',
				objectId: contentId,
				objectType: contentType,
			},
		}).fire();
	}, [contentId, contentType, createAnalyticsEvent]);

	return {
		onVersionClick,
		onVersionHistoryMenuOpen,
		onCopyVersionContent,
		onRestore,
		onRestoreModalClose,
	};
};
